import { httpsCallable } from 'firebase/functions';
import { getFunctionsClient } from '@talk2nick/shared';
import { SUPPORTED_LOCALES, type Locale } from '@talk2nick/core';

import { isTranslationLimited, noteTranslationChars } from './usage';

/**
 * 번역 요청 큐 + 클라이언트 캐시 (PLAN 5-7-4장).
 *
 * 서버 함수(`translateText`)가 이미 `translationCache`(SHA-256 키)로 비용을 막지만,
 * 클라이언트에서도 두 가지를 더 한다:
 *  1) 메모리 캐시 — 같은 문구를 다시 요청하지 않는다(지연 + callable 왕복 제거)
 *  2) 큐 — 동시 3개, 요청 간 최소 150ms 간격 (과금/언어팩 보호)
 *
 * 서버가 캐시 히트를 반환하면 그대로 재사용한다.
 */
export interface TranslationResult {
  text: string;
  /** 서버가 감지한 원문 언어 (미지원/불명이면 'unknown') */
  sourceLang: string;
  targetLang: Locale;
  /** 서버 캐시 히트 여부 */
  cached: boolean;
}

const MAX_CONCURRENT = 3;
const MIN_GAP_MS = 150;

const cache = new Map<string, TranslationResult>();
const queue: Job[] = [];
let inflight = 0;
let nextAllowedAt = 0;

interface Job {
  text: string;
  target: Locale;
  resolve: (value: TranslationResult) => void;
  reject: (error: unknown) => void;
}

interface TranslateResponse {
  text: string;
  sourceLang: string;
  targetLang: string;
  cached: boolean;
}

/** 캐시 키 — 대상 언어 + 원문 (서버는 해시를 쓰지만 클라이언트는 메모리라 원문 그대로) */
function keyOf(text: string, target: Locale): string {
  return `${target}\u0000${text}`;
}

export function cachedTranslation(text: string, target: Locale): TranslationResult | undefined {
  return cache.get(keyOf(text, target));
}

async function callTranslate(text: string, target: Locale): Promise<TranslationResult> {
  const fn = httpsCallable<{ text: string; target: string }, TranslateResponse>(
    getFunctionsClient(),
    'translateText',
  );

  let response;
  try {
    response = await fn({ text, target });
  } catch (error) {
    // 전역 비용 가드레일(PLAN 변경 6장 2번)이 걸린 경우만 따로 잡아낸다.
    // 나머지 실패(한도 초과·네트워크)는 호출부가 조용히 원문으로 대체한다.
    if (isCostPausedError(error)) noteCostPaused();
    throw error;
  }

  const data = response.data;
  const result: TranslationResult = {
    text: data.text,
    sourceLang: data.sourceLang,
    targetLang: (SUPPORTED_LOCALES as readonly string[]).includes(data.targetLang)
      ? (data.targetLang as Locale)
      : target,
    cached: data.cached,
  };
  cache.set(keyOf(text, target), result);
  // 캐시 히트는 서버 과금이 없으므로 사용량에 세지 않는다 (PLAN 5-7-5장)
  if (!result.cached) noteTranslationChars(text.length);
  return result;
}

async function callTranslateBatch(jobs: Job[], target: Locale): Promise<void> {
  const fn = httpsCallable<
    { items: Array<{ id: string; text: string }>; target: string },
    { results: Record<string, TranslateResponse> }
  >(getFunctionsClient(), 'translateBatch');

  try {
    const items = jobs.map((j, idx) => ({ id: String(idx), text: j.text }));
    const response = await fn({ items, target });
    const results = response.data?.results ?? {};

    for (let idx = 0; idx < jobs.length; idx++) {
      const job = jobs[idx];
      if (!job) continue;
      const data = results[String(idx)];
      if (data) {
        const result: TranslationResult = {
          text: data.text,
          sourceLang: data.sourceLang,
          targetLang: (SUPPORTED_LOCALES as readonly string[]).includes(data.targetLang)
            ? (data.targetLang as Locale)
            : target,
          cached: data.cached,
        };
        cache.set(keyOf(job.text, target), result);
        if (!result.cached) noteTranslationChars(job.text.length);
        job.resolve(result);
      } else {
        void callTranslate(job.text, target).then(job.resolve, job.reject);
      }
    }
  } catch (error) {
    if (isCostPausedError(error)) noteCostPaused();
    for (const job of jobs) {
      job.reject(error);
    }
  }
}

// ── 비용 가드레일 통지 (PLAN 변경 6장 2번) ────────────────────────────

/**
 * 서버가 `details.costPaused` 를 실어 보낸 오류만 "전역 정지"로 본다.
 * 문자열 매칭으로 판별하면 문구를 고칠 때 조용히 깨진다.
 */
function isCostPausedError(error: unknown): boolean {
  const details = (error as { details?: unknown }).details;
  return (
    typeof details === 'object' &&
    details !== null &&
    (details as { costPaused?: unknown }).costPaused === true
  );
}

/**
 * 정지 통지를 구독하기 위한 최소 스토어.
 *
 * 번역 실패는 원문으로 대체되므로 사용자는 **아무것도 모른 채** 멈춘 상태가 된다.
 * 그렇지 않으면 "내 설정이 안 먹혔나"로 오해하고 설정 화면을 뒤지른다.
 */
const costPausedListeners = new Set<() => void>();
let costPausedAt = 0;

function noteCostPaused(): void {
  // 같은 세션에서 반복 통지하지 않는다 — 배너가 방마다 반복되면 시끄럽다
  if (Date.now() - costPausedAt < 10 * 60 * 1000) return;
  costPausedAt = Date.now();
  for (const listener of costPausedListeners) listener();
}

export function subscribeCostPaused(listener: () => void): () => void {
  costPausedListeners.add(listener);
  return () => {
    costPausedListeners.delete(listener);
  };
}

export function isCostPaused(): boolean {
  return costPausedAt > 0;
}

let pumpTimer: number | null = null;

function pump(): void {
  if (pumpTimer !== null) return;
  // 50ms 동안 들어오는 번역 요청들을 묶어 단일 배치 호출로 통합한다 (PLAN v0.2)
  pumpTimer = window.setTimeout(() => {
    pumpTimer = null;
    drainQueue();
  }, 50);
}

function drainQueue(): void {
  while (queue.length > 0 && inflight < MAX_CONCURRENT) {
    // 같은 대상 언어(target)를 가진 요청들을 최대 25개까지 묶음
    const first = queue[0];
    if (!first) break;

    const target = first.target;
    const batchJobs: Job[] = [];

    let i = 0;
    while (i < queue.length && batchJobs.length < 25) {
      const qJob = queue[i];
      if (qJob && qJob.target === target) {
        const removed = queue.splice(i, 1)[0];
        if (removed) batchJobs.push(removed);
      } else {
        i++;
      }
    }

    const firstBatchJob = batchJobs[0];
    if (!firstBatchJob) break;

    inflight += 1;
    const gap = Math.max(0, nextAllowedAt - Date.now());
    nextAllowedAt = Date.now() + gap + MIN_GAP_MS;

    window.setTimeout(() => {
      const p =
        batchJobs.length > 1
          ? callTranslateBatch(batchJobs, target)
          : callTranslate(firstBatchJob.text, target).then(
              firstBatchJob.resolve,
              firstBatchJob.reject,
            );

      void p.finally(() => {
        inflight -= 1;
        drainQueue();
      });
    }, gap);
  }
}

/** 번역 요청 (캐시 히트면 즉시). 실패는 호출자가 처리한다. */
export function requestTranslation(text: string, target: Locale): Promise<TranslationResult> {
  const trimmed = text.trim();
  if (!trimmed) return Promise.reject(new Error('empty-text'));

  const hit = cachedTranslation(trimmed, target);
  if (hit) return Promise.resolve(hit);

  // 오늘 한도를 다 썼으면 서버 왕복 없이 즉시 실패시킨다 (5-7-5장: 자동 번역 중지)
  if (isTranslationLimited()) return Promise.reject(new Error('limit-reached'));

  return new Promise<TranslationResult>((resolve, reject) => {
    queue.push({ text: trimmed, target, resolve, reject });
    pump();
  });
}

/**
 * 번역 대상 언어 결정: 설정값이 'auto'면 UI 언어를 쓴다.
 * (서버는 구체적인 대상 언어를 요구하고, 자동 감지는 서버가 원문 언어로 판단한다)
 */
export function resolveTargetLocale(target: Locale | 'auto', uiLanguage: string): Locale {
  if (target !== 'auto') return target;
  const base = uiLanguage.split('-')[0]?.toLowerCase() ?? 'en';
  return (SUPPORTED_LOCALES as readonly string[]).includes(base) ? (base as Locale) : 'en';
}
